import { formatDate, MOMENT_FORMATS } from '../../utils/dateFormat';
import { EVENT_TABS } from './eventsListData';
import { normalizeEventTypeKey, getEventTypeLabel } from './eventTypes';
import { getEventCoverImageFromApi } from './eventImageUtils';
import { formatEventTimeRange, isEventDatePast } from './ManageEvent/eventTimeUtils';
import { normalizeMultilineText } from './ManageEvent/eventFormConstants';
import { isOnlineEventFormat } from './ManageEvent/eventFormatConstants';
import { resolvePartnerApprovalStatus } from '../Partner/Events/partnerEventArchive';

const inferEventStatus = (event) => {
  if (event.status === EVENT_TABS.ARCHIVE || event.status === EVENT_TABS.UPCOMING) {
    return event.status;
  }

  if (event.deletedAt) {
    return EVENT_TABS.ARCHIVE;
  }

  if (isEventDatePast(event.eventDate)) {
    return EVENT_TABS.ARCHIVE;
  }

  return EVENT_TABS.UPCOMING;
};

const formatListDateTime = (event) => {
  const datePart = formatDate(event.eventDate, MOMENT_FORMATS.DATE_WEEKDAY_SHORT) || '';
  const timePart = formatEventTimeRange(event.startTime, event.endTime);

  if (datePart && timePart) return `${datePart}, ${timePart}`;
  return datePart || timePart || '—';
};

const formatPartnerDateLine = (event) =>
  formatDate(event.eventDate, MOMENT_FORMATS.DATE_WEEKDAY) || '—';

/** Partner list date label (e.g. "Wed, Aug 26, 2026"). */
export const formatPartnerEventDateLine = (eventOrDate) => {
  if (eventOrDate && typeof eventOrDate === 'object' && !(eventOrDate instanceof Date)) {
    return formatPartnerDateLine(eventOrDate);
  }
  return formatDate(eventOrDate, MOMENT_FORMATS.DATE_WEEKDAY) || '—';
};

const formatPartnerLocationLine = (event) => {
  if (isOnlineEventFormat(event.eventFormat)) {
    return 'Online';
  }

  return event.venueName ?? '—';
};

const formatPartnerRevenue = (event) => {
  const raw = event.revenue;
  if (raw == null || raw === '') return '—';

  if (typeof raw === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: event.currency ?? 'AED',
      maximumFractionDigits: 0,
    }).format(raw);
  }

  const trimmed = String(raw).trim();
  return /^\$|AED|USD/i.test(trimmed) ? trimmed : `${trimmed} ${event.currency ?? 'AED'}`;
};

const pickPartnerListNumber = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.max(0, Math.round(parsed));
};

const formatPartnerListAttendancePercent = (rsvpSeats, actualAttendance) => {
  if (rsvpSeats != null && rsvpSeats > 0 && actualAttendance != null) {
    return `${Math.round((actualAttendance / rsvpSeats) * 100)}%`;
  }

  return '—';
};

/** Partner list metrics from `rsvpSeats`, `paidSeats`, `actualAttendance`. */
const mapPartnerListMetrics = (event) => {
  const rsvpSeats = pickPartnerListNumber(event.rsvpSeats);
  const paidSeats = pickPartnerListNumber(event.paidSeats);
  const actualAttendance = pickPartnerListNumber(event.actualAttendance);

  return {
    rsvpSeats,
    paidSeats,
    actualAttendance,
    attendancePercent: formatPartnerListAttendancePercent(rsvpSeats, actualAttendance),
  };
};

/** Remaining seats from `availableSeats`, with derived fallback. */
export const resolveAvailableSeats = (event, seatsTotal, seatsTaken = null) => {
  if (Number.isFinite(Number(event?.availableSeats))) {
    return Math.max(0, Number(event.availableSeats));
  }

  const taken =
    seatsTaken ?? (Number.isFinite(Number(event?.rsvpSeats)) ? Number(event.rsvpSeats) : 0);

  return Math.max(0, seatsTotal - taken);
};

export const mapApiEventToListItem = (event, { partnerList = false } = {}) => {
  const id = String(event.id ?? '');
  const seatsTotal = Number(event.totalSeats ?? 0);
  const rsvpSeats = pickPartnerListNumber(event.rsvpSeats) ?? 0;
  const seatsAvailable = resolveAvailableSeats(event, seatsTotal, rsvpSeats);
  const partnerMetrics = partnerList ? mapPartnerListMetrics(event) : null;
  const status = event.status ?? null;
  const deletedAt = event.deletedAt ?? null;
  const isArchived = Boolean(deletedAt);
  const partnerApprovalStatus = partnerList ? resolvePartnerApprovalStatus(event) : null;
  const approvalStatus = partnerApprovalStatus ?? (isArchived ? 'archived' : status);
  const venueName = event.venueName ?? '';
  const partner = event.partner ?? event.createdByPartner ?? event.createdByPartnerProfile ?? null;

  return {
    id,
    title: event.title ?? '',
    type: normalizeEventTypeKey(event.type),
    typeLabel: getEventTypeLabel(event.type),
    description: event.description ?? '',
    wingmanDescription: normalizeMultilineText(event.wingmanDescription ?? ''),
    dateTime: formatListDateTime(event),
    dateLine: formatPartnerDateLine(event),
    locationLine: formatPartnerLocationLine(event),
    venueName,
    location: venueName,
    fullAddress: event.location ?? '',
    eventFormat: event.eventFormat ?? null,
    onlineMeetingLink: event.onlineMeetingLink ?? null,
    seatsAvailable,
    totalSeats: seatsTotal,
    ...(partnerMetrics ?? {}),
    revenue: formatPartnerRevenue(event),
    published: Boolean(event.published),
    deletedAt,
    isArchived,
    approvalStatus,
    status: partnerApprovalStatus || status || inferEventStatus(event),
    coverImageUrl: getEventCoverImageFromApi(event),
    imageUrl: getEventCoverImageFromApi(event),
    eventDate: event.eventDate ?? null,
    startTime: event.startTime ?? '',
    endTime: event.endTime ?? '',
    price: event.price ?? null,
    currency: event.currency ?? null,
    createdByPartnerId: event.createdByPartnerId ?? null,
    createdByName: partner?.fullName ?? partner?.name ?? '',
    createdByAvatarUrl: partner?.avatarUrl ?? partner?.logoUrl ?? '',
    enableWaitlist: Boolean(event.enableWaitlist),
    inviteOnly: Boolean(event.inviteOnly),
    requireJoinApproval: Boolean(event.requireJoinApproval),
    attendeeEligibility: event.attendeeEligibility ?? null,
    memberMatching: Boolean(event.memberMatching),
    refundPolicy: event.refundPolicy ?? null,
    actualAttendance: event.actualAttendance ?? null,
    availableSeats: seatsAvailable,
    moderationRecords: Array.isArray(event.moderationRecords) ? event.moderationRecords : [],
    createdAt: event.createdAt ?? null,
    updatedAt: event.updatedAt ?? null,
  };
};

const getResponseRoot = (payload) => payload?.data ?? payload;

/**
 * GET /admin/events/:id
 * { data: { id, title, coverImageUrl, menuItems, ... } }
 */
export const normalizeEventDetailResponse = (payload) => {
  const root = getResponseRoot(payload);

  if (!root?.id) {
    return null;
  }

  return {
    ...root,
    id: String(root.id),
    menuItems: root.menuItems ?? { starter: [], main: [], desert: [] },
  };
};

/**
 * GET /admin/events
 * { data: { events, total, limit, page } }
 */
export const mapApiEventsResponse = (payload, { partnerList = false } = {}) => {
  const root = getResponseRoot(payload);
  const items = root?.events ?? root?.items ?? root?.results ?? (Array.isArray(root) ? root : []);
  const total = Number(root?.total ?? root?.count ?? items.length);
  const limit = Number(root?.limit ?? items.length) || 1;
  const page = Number(root?.page ?? 1);
  const totalPages = Number(root?.totalPages ?? Math.max(1, Math.ceil(total / limit)));

  return {
    events: items.map((event) => mapApiEventToListItem(event, { partnerList })),
    total,
    limit,
    page,
    totalPages,
  };
};
